import { Upload, Button, Form, message, Typography, Space, Alert } from "antd";
import { UploadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { uploadMenu } from "../actions/merchant_actions";
import ContentLayout from "../components/layout/ContentLayout";
import * as XLSX from "xlsx";

const { Title } = Typography;

const REQUIRED_COLUMNS = [
  "name",
  "currency_code",
  "minimum_payment_amount",
  "maximum_payment_amount",
];

const OrdersForm = () => {
  const navigate = useNavigate();
  const { accUrn } = useParams();

  const account_urn = accUrn;

  const beforeUpload = async (file) => {
    const isXlsx =
      file.name.endsWith(".xlsx") &&
      (file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "");

    const isLt8M = file.size / 1024 / 1024 < 8;

    if (!isXlsx) {
      message.error("Only .xlsx files are allowed.");
      return Upload.LIST_IGNORE;
    }

    if (!isLt8M) {
      message.error("File must be smaller than 8MB.");
      return Upload.LIST_IGNORE;
    }

    const isValid = await validateFileColumns(file);
    if (!isValid) {
      message.error("Invalid file. Ensure required columns are present.");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const validateFileColumns = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        const headers = rows[0]?.map((h) => String(h).trim().toLowerCase());

        const isValid = REQUIRED_COLUMNS.every((col) => headers.includes(col));

        resolve(isValid);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const customUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await uploadMenu({ account_urn, data: formData });
      message.success(`${file.name} uploaded successfully.`);
      onSuccess("ok");
      navigate(-1);
    } catch (err) {
      console.error(err);
      message.error("Upload failed.");
      onError(err);
    }
  };

  return (
    <ContentLayout
      header={
        <div className="header">
          <Title level={3}>Upload Order File</Title>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Return
          </Button>
        </div>
      }
    >
      <div className="order-form-wrapper">
        <Alert
          type="info"
          showIcon
          message="Instructions"
          description={
            <ul style={{ marginLeft: "16px" }}>
              <li>
                Only <strong>.xlsx</strong> files are allowed
              </li>
              <li>
                Max size: <strong>8MB</strong>
              </li>
              <li>
                Required columns (case insensitive):{" "}
                <code>
                  name, currency_code, minimum_payment_amount,
                  maximum_payment_amount
                </code>
              </li>
              <li>Headers must exist in the first row</li>
            </ul>
          }
          style={{ marginBottom: 20 }}
        />

        <Form layout="vertical">
          <Form.Item label="Select .xlsx File (Max 8MB)">
            <Upload
              customRequest={customUpload}
              beforeUpload={beforeUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>
        </Form>
      </div>
    </ContentLayout>
  );
};

export default OrdersForm;
